export type Locale = 'en' | 'es' | 'fr' | 'de' | 'pt'

export interface Translation {
  locale: Locale
  name: string
  flag: string
  form: {
    country: string
    squareFootage: string
    squareFootageHelp: string
    material: string
    removeOldRoof: string
    yes: string
    no: string
    existingLayers: string
    layer1: string
    layer2: string
    layer3: string
    numStories: string
    story1: string
    story2: string
    story3: string
    roofPitch: string
    pitchLow: string
    pitchMedium: string
    pitchSteep: string
    pitchHelp: string
    roofCondition: string
    conditionGood: string
    conditionFair: string
    conditionPoor: string
    chimneys: string
    chimneysHelp: string
    skylights: string
    skylightsHelp: string
    roofComplexity: string
    valleysFew: string
    valleysModerate: string
    valleysMany: string
    valleysHelp: string
    timeline: string
    timelineFlexible: string
    timelineSoon: string
    timelineUrgent: string
    timelineHelp: string
    solarTooltipTitle: string
    solarFederalCredit: string
    solarEnergySavings: string
    solarPayback: string
    solarPropertyValue: string
    greenTooltipTitle: string
    greenTaxCredits: string
    greenEnergySavings: string
    greenStormwater: string
    greenLifespan: string
    greenPropertyValue: string
    calculateBtn: string
    calculating: string
  }
  results: {
    downloadPDF: string
    print: string
    email: string
    save: string
    saved: string
    signInToSave: string
    totalCostRange: string
    costBreakdown: string
    baseInstallation: string
    included: string
    tearOff: string
    layer: string
    layers: string
    pitchAdjustment: string
    multiStory: string
    chimneyFlashing: string
    skylightFlashing: string
    complexityFee: string
    valleys: string
    urgencyPremium: string
    estimateNote: string
    materialComparison: string
    material: string
    costPerSqFt: string
    lifespan: string
    bestFor: string
    keyBenefits: string
    ecoNote: string
    selected: string
    shareTitle: string
    recipientEmail: string
    emailPlaceholder: string
    recipientNote: string
    whatWillBeSent: string
    pdfEstimate: string
    maintenanceChart: string
    projectSpecs: string
    cancel: string
    sendEmail: string
    sending: string
    emailSentSuccess: string
    emailFailed: string
    maintenanceTitle: string
    maintenanceSubtitle: string
    installCost: string
    maintenanceCost: string
    totalOwnership: string
    maintenanceDetails: string
    yourChoice: string
    tipTitle: string
    tipBody: string
    maintenanceNote: string
    ecoFriendly: string
  }
  ai: {
    title: string
    subtitle: string
    climateLabel: string
    climatePlaceholder: string
    budgetLabel: string
    budgetPlaceholder: string
    roofTypeLabel: string
    roofTypePlaceholder: string
    priorityLabel: string
    getRecommendation: string
    getting: string
    recommendedFor: string
    applyMaterial: string
    applied: string
    error: string
    climateRequired: string
    budgetRequired: string
    hotHumid: string
    coldSnowy: string
    temperate: string
    desert: string
    coastal: string
    mixed: string
    durability: string
    energySavings: string
    aesthetics: string
    ecoFriendly: string
    lowMaintenance: string
  }
  nav: {
    home: string
    metal: string
    commercial: string
    repair: string
    virtual: string
    blog: string
    savedEstimates: string
    signIn: string
    signOut: string
    language: string
  }
}

const en: Translation = {
  locale: 'en',
  name: 'English',
  flag: '🇺🇸',
  form: {
    country: 'Country',
    squareFootage: 'Roof Square Footage',
    squareFootageHelp: 'Average homes: 1,500–3,000 sq ft',
    material: 'Roofing Material',
    removeOldRoof: 'Remove Old Roof?',
    yes: 'Yes',
    no: 'No',
    existingLayers: 'Existing Roof Layers',
    layer1: '1 Layer',
    layer2: '2 Layers',
    layer3: '3+ Layers',
    numStories: 'Number of Stories',
    story1: '1 Story',
    story2: '2 Stories',
    story3: '3+ Stories',
    roofPitch: 'Roof Pitch / Slope',
    pitchLow: 'Low Pitch (2:12 – 4:12)',
    pitchMedium: 'Medium Pitch (4:12 – 8:12)',
    pitchSteep: 'Steep Pitch (8:12+)',
    pitchHelp: 'Steeper roofs increase labor costs',
    roofCondition: 'Current Roof Condition',
    conditionGood: 'Good – Minor wear',
    conditionFair: 'Fair – Some damage',
    conditionPoor: 'Poor – Significant issues',
    chimneys: 'Number of Chimneys',
    chimneysHelp: 'Flashing: ~{symbol}350 per chimney',
    skylights: 'Number of Skylights',
    skylightsHelp: 'Flashing: ~{symbol}250 per skylight',
    roofComplexity: 'Roof Complexity (Valleys)',
    valleysFew: 'Few Valleys – Simple Design',
    valleysModerate: 'Moderate – Standard Home',
    valleysMany: 'Many Valleys – Complex Design',
    valleysHelp: 'More valleys increase installation complexity',
    timeline: 'Project Timeline',
    timelineFlexible: 'Flexible – Planning ahead',
    timelineSoon: 'Soon – Within 2–4 weeks',
    timelineUrgent: 'Urgent – Emergency repair',
    timelineHelp: 'Urgent timelines may include premium fees',
    solarTooltipTitle: 'Long-Term ROI & Tax Benefits',
    solarFederalCredit: 'Federal Tax Credit: 30% of installation costs (through 2032)',
    solarEnergySavings: 'Energy Savings: $1,000–$2,000/year average electricity savings',
    solarPayback: 'Payback Period: 10–15 years with incentives',
    solarPropertyValue: 'Property Value: Increases home value by 4–6%',
    greenTooltipTitle: 'Sustainability Benefits & Incentives',
    greenTaxCredits: 'Tax Credits: Varies by state; check local green building incentives',
    greenEnergySavings: 'Energy Savings: 20–30% reduction in cooling costs',
    greenStormwater: 'Stormwater Credits: Many cities offer tax rebates for rainwater management',
    greenLifespan: 'Lifespan: Extends roof membrane life by 2–3x (30–50 years)',
    greenPropertyValue: 'Property Value: Premium pricing in eco-conscious markets',
    calculateBtn: 'Calculate My Estimate',
    calculating: 'Calculating…',
  },
  results: {
    downloadPDF: 'Download PDF',
    print: 'Print',
    email: 'Email',
    save: 'Save',
    saved: 'Saved!',
    signInToSave: 'Sign in to save your estimates for future reference',
    totalCostRange: 'Total Estimated Cost Range:',
    costBreakdown: 'Cost Breakdown',
    baseInstallation: 'Base Material & Installation:',
    included: 'Included',
    tearOff: 'Tear Off',
    layer: 'layer',
    layers: 'layers',
    pitchAdjustment: 'Pitch Adjustment',
    multiStory: 'Multi-Story Labor',
    chimneyFlashing: 'Chimney Flashing',
    skylightFlashing: 'Skylight Flashing',
    complexityFee: 'Complexity Fee',
    valleys: 'valleys',
    urgencyPremium: 'Urgency Premium',
    estimateNote: '*Estimates based on {sqft} sq ft in {postalLabel} {zip} ({country}). Actual costs vary by location, contractor rates, permits, and unforeseen conditions. Currency: {symbol}. This calculator provides planning guidance only.',
    materialComparison: 'Material Comparison Chart',
    material: 'Material',
    costPerSqFt: 'Cost/sq ft',
    lifespan: 'Lifespan',
    bestFor: 'Best For',
    keyBenefits: 'Key Benefits',
    ecoNote: '= Eco-friendly option with sustainability benefits',
    selected: 'Selected',
    shareTitle: 'Share Estimate via Email',
    recipientEmail: 'Recipient Email Address',
    emailPlaceholder: 'Enter email address',
    recipientNote: 'Note: Recipient must be a registered user on our platform',
    whatWillBeSent: 'What will be sent:',
    pdfEstimate: 'Complete PDF estimate with cost breakdown',
    maintenanceChart: '30-year maintenance cost comparison chart',
    projectSpecs: 'Detailed project specifications',
    cancel: 'Cancel',
    sendEmail: 'Send Email',
    sending: 'Sending…',
    emailSentSuccess: 'Email sent successfully!',
    emailFailed: 'Failed to send email. The recipient must be a registered user.',
    maintenanceTitle: '30-Year Maintenance Cost Comparison',
    maintenanceSubtitle: 'Total ownership costs include installation + maintenance over 30 years. Lower maintenance materials often offset higher upfront costs.',
    installCost: 'Installation Cost',
    maintenanceCost: '30-Year Maintenance',
    totalOwnership: 'Total 30-Year Cost',
    maintenanceDetails: 'Maintenance Details',
    yourChoice: 'Your Choice',
    tipTitle: 'Smart Investment Tip:',
    tipBody: 'Materials like Metal, Slate, and Recycled Metal have higher upfront costs but significantly lower maintenance over 30 years. Consider total ownership cost, not just installation price, when choosing your roofing material.',
    maintenanceNote: '* Maintenance costs are estimates based on industry averages for a {sqft} sq ft roof. Actual costs vary by climate, installation quality, and local contractor rates.',
    ecoFriendly: 'Eco-friendly',
  },
  ai: {
    title: '🤖 AI Material Recommender',
    subtitle: 'Get personalized roofing material suggestions based on your climate and budget',
    climateLabel: 'Your Climate',
    climatePlaceholder: 'Select your climate',
    budgetLabel: 'Budget Range',
    budgetPlaceholder: 'Select your budget',
    roofTypeLabel: 'Roof Type (optional)',
    roofTypePlaceholder: 'e.g., residential, commercial, flat',
    priorityLabel: 'Your Priority',
    getRecommendation: 'Get AI Recommendation',
    getting: 'Getting recommendation…',
    recommendedFor: 'Recommended for your {climate} climate & {budget} budget',
    applyMaterial: 'Apply Material to Calculator',
    applied: '✓ Applied!',
    error: 'Failed to get recommendation. Please try again.',
    climateRequired: 'Please select a climate',
    budgetRequired: 'Please select a budget range',
    hotHumid: '☀️ Hot & Humid',
    coldSnowy: '❄️ Cold & Snowy',
    temperate: '🌤️ Temperate',
    desert: '🏜️ Desert / Arid',
    coastal: '🌊 Coastal',
    mixed: '🌦️ Mixed Seasons',
    durability: '💪 Durability',
    energySavings: '⚡ Energy Savings',
    aesthetics: '🎨 Aesthetics',
    ecoFriendly: '🌱 Eco-Friendly',
    lowMaintenance: '🔧 Low Maintenance',
  },
  nav: {
    home: 'Home',
    metal: 'Metal Roofing',
    commercial: 'Commercial',
    repair: 'Repair & Maintenance',
    virtual: 'Virtual Estimate',
    blog: 'Blog',
    savedEstimates: 'Saved Estimates',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    language: 'Language',
  },
}

const es: Translation = {
  locale: 'es',
  name: 'Español',
  flag: '🇪🇸',
  form: {
    country: 'País',
    squareFootage: 'Metros Cuadrados del Techo',
    squareFootageHelp: 'Hogares promedio: 1,500–3,000 pies cuadrados',
    material: 'Material del Techo',
    removeOldRoof: '¿Quitar el Techo Viejo?',
    yes: 'Sí',
    no: 'No',
    existingLayers: 'Capas Existentes del Techo',
    layer1: '1 Capa',
    layer2: '2 Capas',
    layer3: '3+ Capas',
    numStories: 'Número de Pisos',
    story1: '1 Piso',
    story2: '2 Pisos',
    story3: '3+ Pisos',
    roofPitch: 'Inclinación del Techo',
    pitchLow: 'Baja (2:12 – 4:12)',
    pitchMedium: 'Media (4:12 – 8:12)',
    pitchSteep: 'Pronunciada (8:12+)',
    pitchHelp: 'Los techos más inclinados aumentan los costos de mano de obra',
    roofCondition: 'Estado Actual del Techo',
    conditionGood: 'Bueno – Desgaste mínimo',
    conditionFair: 'Regular – Algunos daños',
    conditionPoor: 'Malo – Problemas significativos',
    chimneys: 'Número de Chimeneas',
    chimneysHelp: 'Sellado: ~{symbol}350 por chimenea',
    skylights: 'Número de Tragaluces',
    skylightsHelp: 'Sellado: ~{symbol}250 por tragaluz',
    roofComplexity: 'Complejidad del Techo (Valles)',
    valleysFew: 'Pocos Valles – Diseño Simple',
    valleysModerate: 'Moderado – Casa Estándar',
    valleysMany: 'Muchos Valles – Diseño Complejo',
    valleysHelp: 'Más valles aumentan la complejidad de instalación',
    timeline: 'Cronograma del Proyecto',
    timelineFlexible: 'Flexible – Planificación futura',
    timelineSoon: 'Pronto – Dentro de 2–4 semanas',
    timelineUrgent: 'Urgente – Reparación de emergencia',
    timelineHelp: 'Los plazos urgentes pueden incluir tarifas adicionales',
    solarTooltipTitle: 'ROI a Largo Plazo y Beneficios Fiscales',
    solarFederalCredit: 'Crédito Fiscal Federal: 30% del costo de instalación (hasta 2032)',
    solarEnergySavings: 'Ahorro Energético: $1,000–$2,000/año promedio de ahorro eléctrico',
    solarPayback: 'Período de Recuperación: 10–15 años con incentivos',
    solarPropertyValue: 'Valor de la Propiedad: Aumenta el valor del hogar en un 4–6%',
    greenTooltipTitle: 'Beneficios de Sostenibilidad e Incentivos',
    greenTaxCredits: 'Créditos Fiscales: Varía por estado; verifique incentivos locales de construcción verde',
    greenEnergySavings: 'Ahorro Energético: Reducción del 20–30% en costos de refrigeración',
    greenStormwater: 'Créditos por Agua de Lluvia: Muchas ciudades ofrecen rebajas fiscales',
    greenLifespan: 'Vida Útil: Extiende la membrana del techo 2–3 veces (30–50 años)',
    greenPropertyValue: 'Valor de la Propiedad: Precio premium en mercados eco-conscientes',
    calculateBtn: 'Calcular Mi Estimación',
    calculating: 'Calculando…',
  },
  results: {
    downloadPDF: 'Descargar PDF',
    print: 'Imprimir',
    email: 'Correo',
    save: 'Guardar',
    saved: '¡Guardado!',
    signInToSave: 'Inicia sesión para guardar tus estimaciones',
    totalCostRange: 'Rango Total de Costo Estimado:',
    costBreakdown: 'Desglose de Costos',
    baseInstallation: 'Material Base e Instalación:',
    included: 'Incluido',
    tearOff: 'Remoción',
    layer: 'capa',
    layers: 'capas',
    pitchAdjustment: 'Ajuste de Inclinación',
    multiStory: 'Mano de Obra Multi-Piso',
    chimneyFlashing: 'Sellado de Chimenea',
    skylightFlashing: 'Sellado de Tragaluz',
    complexityFee: 'Tarifa de Complejidad',
    valleys: 'valles',
    urgencyPremium: 'Prima de Urgencia',
    estimateNote: '*Estimaciones basadas en {sqft} pies cuadrados en {postalLabel} {zip} ({country}). Los costos reales varían. Moneda: {symbol}.',
    materialComparison: 'Tabla Comparativa de Materiales',
    material: 'Material',
    costPerSqFt: 'Costo/pie²',
    lifespan: 'Vida Útil',
    bestFor: 'Ideal Para',
    keyBenefits: 'Beneficios Clave',
    ecoNote: '= Opción ecológica con beneficios de sostenibilidad',
    selected: 'Seleccionado',
    shareTitle: 'Compartir Estimación por Correo',
    recipientEmail: 'Correo del Destinatario',
    emailPlaceholder: 'Ingrese la dirección de correo',
    recipientNote: 'Nota: El destinatario debe ser usuario registrado',
    whatWillBeSent: 'Qué se enviará:',
    pdfEstimate: 'Estimación PDF completa con desglose de costos',
    maintenanceChart: 'Gráfico de costos de mantenimiento a 30 años',
    projectSpecs: 'Especificaciones detalladas del proyecto',
    cancel: 'Cancelar',
    sendEmail: 'Enviar Correo',
    sending: 'Enviando…',
    emailSentSuccess: '¡Correo enviado exitosamente!',
    emailFailed: 'No se pudo enviar el correo. El destinatario debe ser usuario registrado.',
    maintenanceTitle: 'Comparación de Costos de Mantenimiento a 30 Años',
    maintenanceSubtitle: 'Los costos totales incluyen instalación + mantenimiento en 30 años.',
    installCost: 'Costo de Instalación',
    maintenanceCost: 'Mantenimiento 30 Años',
    totalOwnership: 'Costo Total 30 Años',
    maintenanceDetails: 'Detalles de Mantenimiento',
    yourChoice: 'Tu Elección',
    tipTitle: 'Consejo de Inversión Inteligente:',
    tipBody: 'Materiales como Metal, Pizarra y Metal Reciclado tienen costos iniciales más altos pero mantenimiento significativamente menor en 30 años.',
    maintenanceNote: '* Los costos de mantenimiento son estimaciones basadas en promedios de la industria para un techo de {sqft} pies cuadrados.',
    ecoFriendly: 'Ecológico',
  },
  ai: {
    title: '🤖 Recomendador de Materiales con IA',
    subtitle: 'Obtén sugerencias personalizadas según tu clima y presupuesto',
    climateLabel: 'Tu Clima',
    climatePlaceholder: 'Selecciona tu clima',
    budgetLabel: 'Rango de Presupuesto',
    budgetPlaceholder: 'Selecciona tu presupuesto',
    roofTypeLabel: 'Tipo de Techo (opcional)',
    roofTypePlaceholder: 'ej. residencial, comercial, plano',
    priorityLabel: 'Tu Prioridad',
    getRecommendation: 'Obtener Recomendación IA',
    getting: 'Obteniendo recomendación…',
    recommendedFor: 'Recomendado para tu clima {climate} y presupuesto {budget}',
    applyMaterial: 'Aplicar Material a la Calculadora',
    applied: '✓ ¡Aplicado!',
    error: 'No se pudo obtener la recomendación. Inténtalo de nuevo.',
    climateRequired: 'Por favor selecciona un clima',
    budgetRequired: 'Por favor selecciona un rango de presupuesto',
    hotHumid: '☀️ Caliente y Húmedo',
    coldSnowy: '❄️ Frío y Nevado',
    temperate: '🌤️ Templado',
    desert: '🏜️ Desértico / Árido',
    coastal: '🌊 Costero',
    mixed: '🌦️ Estaciones Mixtas',
    durability: '💪 Durabilidad',
    energySavings: '⚡ Ahorro Energético',
    aesthetics: '🎨 Estética',
    ecoFriendly: '🌱 Ecológico',
    lowMaintenance: '🔧 Bajo Mantenimiento',
  },
  nav: {
    home: 'Inicio',
    metal: 'Techos Metálicos',
    commercial: 'Comercial',
    repair: 'Reparación',
    virtual: 'Estimación Virtual',
    blog: 'Blog',
    savedEstimates: 'Estimaciones Guardadas',
    signIn: 'Iniciar Sesión',
    signOut: 'Cerrar Sesión',
    language: 'Idioma',
  },
}

const fr: Translation = {
  locale: 'fr',
  name: 'Français',
  flag: '🇫🇷',
  form: {
    country: 'Pays',
    squareFootage: 'Surface du Toit (pi²)',
    squareFootageHelp: 'Maisons typiques : 1 500–3 000 pi²',
    material: 'Matériau de Toiture',
    removeOldRoof: 'Enlever l\'ancien toit ?',
    yes: 'Oui',
    no: 'Non',
    existingLayers: 'Couches de toit existantes',
    layer1: '1 Couche',
    layer2: '2 Couches',
    layer3: '3+ Couches',
    numStories: 'Nombre d\'étages',
    story1: '1 Étage',
    story2: '2 Étages',
    story3: '3+ Étages',
    roofPitch: 'Pente du Toit',
    pitchLow: 'Faible (2:12 – 4:12)',
    pitchMedium: 'Moyenne (4:12 – 8:12)',
    pitchSteep: 'Forte (8:12+)',
    pitchHelp: 'Les toits plus pentus augmentent les coûts de main-d\'œuvre',
    roofCondition: 'État Actuel du Toit',
    conditionGood: 'Bon – Usure mineure',
    conditionFair: 'Moyen – Quelques dégâts',
    conditionPoor: 'Mauvais – Problèmes importants',
    chimneys: 'Nombre de Cheminées',
    chimneysHelp: 'Solins : ~{symbol}350 par cheminée',
    skylights: 'Nombre de Velux',
    skylightsHelp: 'Solins : ~{symbol}250 par velux',
    roofComplexity: 'Complexité du Toit (Noues)',
    valleysFew: 'Peu de Noues – Design Simple',
    valleysModerate: 'Modéré – Maison Standard',
    valleysMany: 'Beaucoup de Noues – Design Complexe',
    valleysHelp: 'Plus de noues augmentent la complexité',
    timeline: 'Calendrier du Projet',
    timelineFlexible: 'Flexible – Planification à l\'avance',
    timelineSoon: 'Bientôt – Dans 2–4 semaines',
    timelineUrgent: 'Urgent – Réparation d\'urgence',
    timelineHelp: 'Les délais urgents peuvent inclure des frais supplémentaires',
    solarTooltipTitle: 'ROI à Long Terme et Avantages Fiscaux',
    solarFederalCredit: 'Crédit Fiscal Fédéral : 30% des coûts d\'installation (jusqu\'en 2032)',
    solarEnergySavings: 'Économies Énergétiques : 1 000–2 000$/an en moyenne',
    solarPayback: 'Période de Remboursement : 10–15 ans avec les incitations',
    solarPropertyValue: 'Valeur Immobilière : Augmente la valeur de 4–6%',
    greenTooltipTitle: 'Avantages Durables et Incitations',
    greenTaxCredits: 'Crédits Fiscaux : Varie selon la région',
    greenEnergySavings: 'Économies Énergétiques : Réduction de 20–30% des coûts de climatisation',
    greenStormwater: 'Crédits Eaux Pluviales : Remises fiscales dans de nombreuses villes',
    greenLifespan: 'Durée de Vie : Prolonge la membrane de 2–3x (30–50 ans)',
    greenPropertyValue: 'Valeur Immobilière : Prix premium sur les marchés éco-conscients',
    calculateBtn: 'Calculer Mon Estimation',
    calculating: 'Calcul en cours…',
  },
  results: {
    downloadPDF: 'Télécharger PDF',
    print: 'Imprimer',
    email: 'Email',
    save: 'Sauvegarder',
    saved: 'Sauvegardé !',
    signInToSave: 'Connectez-vous pour sauvegarder vos estimations',
    totalCostRange: 'Fourchette de Coût Estimé :',
    costBreakdown: 'Détail des Coûts',
    baseInstallation: 'Matériau de Base et Installation :',
    included: 'Inclus',
    tearOff: 'Dépose',
    layer: 'couche',
    layers: 'couches',
    pitchAdjustment: 'Ajustement de Pente',
    multiStory: 'Main-d\'œuvre Multi-Étages',
    chimneyFlashing: 'Solin de Cheminée',
    skylightFlashing: 'Solin de Velux',
    complexityFee: 'Frais de Complexité',
    valleys: 'noues',
    urgencyPremium: 'Supplément d\'Urgence',
    estimateNote: '*Estimations basées sur {sqft} pi² en {postalLabel} {zip} ({country}). Monnaie : {symbol}.',
    materialComparison: 'Tableau Comparatif des Matériaux',
    material: 'Matériau',
    costPerSqFt: 'Coût/pi²',
    lifespan: 'Durée de Vie',
    bestFor: 'Idéal Pour',
    keyBenefits: 'Avantages Clés',
    ecoNote: '= Option écologique avec bénéfices durables',
    selected: 'Sélectionné',
    shareTitle: 'Partager l\'Estimation par Email',
    recipientEmail: 'Adresse Email du Destinataire',
    emailPlaceholder: 'Saisir l\'adresse email',
    recipientNote: 'Note : Le destinataire doit être un utilisateur enregistré',
    whatWillBeSent: 'Ce qui sera envoyé :',
    pdfEstimate: 'Estimation PDF complète avec détail des coûts',
    maintenanceChart: 'Graphique comparatif de maintenance sur 30 ans',
    projectSpecs: 'Spécifications détaillées du projet',
    cancel: 'Annuler',
    sendEmail: 'Envoyer',
    sending: 'Envoi…',
    emailSentSuccess: 'Email envoyé avec succès !',
    emailFailed: 'Échec de l\'envoi. Le destinataire doit être un utilisateur enregistré.',
    maintenanceTitle: 'Comparaison des Coûts de Maintenance sur 30 Ans',
    maintenanceSubtitle: 'Les coûts totaux incluent l\'installation + la maintenance sur 30 ans.',
    installCost: 'Coût d\'Installation',
    maintenanceCost: 'Maintenance 30 Ans',
    totalOwnership: 'Coût Total 30 Ans',
    maintenanceDetails: 'Détails de Maintenance',
    yourChoice: 'Votre Choix',
    tipTitle: 'Conseil d\'Investissement :',
    tipBody: 'Les matériaux comme le Métal, l\'Ardoise et le Métal Recyclé ont des coûts initiaux plus élevés mais une maintenance nettement inférieure sur 30 ans.',
    maintenanceNote: '* Les coûts de maintenance sont des estimations basées sur les moyennes du secteur pour un toit de {sqft} pi².',
    ecoFriendly: 'Écologique',
  },
  ai: {
    title: '🤖 Recommandateur de Matériaux IA',
    subtitle: 'Obtenez des suggestions personnalisées selon votre climat et budget',
    climateLabel: 'Votre Climat',
    climatePlaceholder: 'Sélectionnez votre climat',
    budgetLabel: 'Fourchette de Budget',
    budgetPlaceholder: 'Sélectionnez votre budget',
    roofTypeLabel: 'Type de Toit (optionnel)',
    roofTypePlaceholder: 'ex. résidentiel, commercial, plat',
    priorityLabel: 'Votre Priorité',
    getRecommendation: 'Obtenir une Recommandation IA',
    getting: 'Obtention de la recommandation…',
    recommendedFor: 'Recommandé pour votre climat {climate} et budget {budget}',
    applyMaterial: 'Appliquer le Matériau',
    applied: '✓ Appliqué !',
    error: 'Impossible d\'obtenir la recommandation. Veuillez réessayer.',
    climateRequired: 'Veuillez sélectionner un climat',
    budgetRequired: 'Veuillez sélectionner une fourchette de budget',
    hotHumid: '☀️ Chaud et Humide',
    coldSnowy: '❄️ Froid et Neigeux',
    temperate: '🌤️ Tempéré',
    desert: '🏜️ Désertique / Aride',
    coastal: '🌊 Côtier',
    mixed: '🌦️ Saisons Mixtes',
    durability: '💪 Durabilité',
    energySavings: '⚡ Économies Énergétiques',
    aesthetics: '🎨 Esthétique',
    ecoFriendly: '🌱 Écologique',
    lowMaintenance: '🔧 Faible Maintenance',
  },
  nav: {
    home: 'Accueil',
    metal: 'Toiture Métallique',
    commercial: 'Commercial',
    repair: 'Réparation',
    virtual: 'Estimation Virtuelle',
    blog: 'Blog',
    savedEstimates: 'Estimations Sauvegardées',
    signIn: 'Se Connecter',
    signOut: 'Se Déconnecter',
    language: 'Langue',
  },
}

const de: Translation = {
  locale: 'de',
  name: 'Deutsch',
  flag: '🇩🇪',
  form: {
    country: 'Land',
    squareFootage: 'Dachfläche (Quadratfuß)',
    squareFootageHelp: 'Durchschnittliche Häuser: 1.500–3.000 sq ft',
    material: 'Dachmaterial',
    removeOldRoof: 'Altes Dach entfernen?',
    yes: 'Ja',
    no: 'Nein',
    existingLayers: 'Vorhandene Dachschichten',
    layer1: '1 Schicht',
    layer2: '2 Schichten',
    layer3: '3+ Schichten',
    numStories: 'Anzahl der Stockwerke',
    story1: '1 Stockwerk',
    story2: '2 Stockwerke',
    story3: '3+ Stockwerke',
    roofPitch: 'Dachneigung',
    pitchLow: 'Flach (2:12 – 4:12)',
    pitchMedium: 'Mittel (4:12 – 8:12)',
    pitchSteep: 'Steil (8:12+)',
    pitchHelp: 'Steilere Dächer erhöhen die Arbeitskosten',
    roofCondition: 'Aktueller Dachzustand',
    conditionGood: 'Gut – Geringfügiger Verschleiß',
    conditionFair: 'Mittel – Einige Schäden',
    conditionPoor: 'Schlecht – Erhebliche Probleme',
    chimneys: 'Anzahl der Schornsteine',
    chimneysHelp: 'Abdichtung: ~{symbol}350 pro Schornstein',
    skylights: 'Anzahl der Dachfenster',
    skylightsHelp: 'Abdichtung: ~{symbol}250 pro Dachfenster',
    roofComplexity: 'Dachkomplexität (Kehlen)',
    valleysFew: 'Wenige Kehlen – Einfaches Design',
    valleysModerate: 'Moderat – Standardhaus',
    valleysMany: 'Viele Kehlen – Komplexes Design',
    valleysHelp: 'Mehr Kehlen erhöhen die Installationskomplexität',
    timeline: 'Projektzeitraum',
    timelineFlexible: 'Flexibel – Vorausplanung',
    timelineSoon: 'Bald – Innerhalb von 2–4 Wochen',
    timelineUrgent: 'Dringend – Notreparatur',
    timelineHelp: 'Dringende Zeitpläne können Zuschläge beinhalten',
    solarTooltipTitle: 'Langfristiger ROI & Steuervorteile',
    solarFederalCredit: 'Bundessteuerkredit: 30% der Installationskosten (bis 2032)',
    solarEnergySavings: 'Energieeinsparungen: 1.000–2.000$/Jahr durchschnittlich',
    solarPayback: 'Amortisationszeit: 10–15 Jahre mit Förderungen',
    solarPropertyValue: 'Immobilienwert: Erhöht den Hauswert um 4–6%',
    greenTooltipTitle: 'Nachhaltigkeitsvorteile & Anreize',
    greenTaxCredits: 'Steuergutschriften: Je nach Bundesland variierend',
    greenEnergySavings: 'Energieeinsparungen: 20–30% Reduzierung der Kühlkosten',
    greenStormwater: 'Regenwasser-Gutschriften: Viele Städte bieten Steuerrückerstattungen',
    greenLifespan: 'Lebensdauer: Verlängert die Dachmembran 2–3-fach (30–50 Jahre)',
    greenPropertyValue: 'Immobilienwert: Premiumpreise auf umweltbewussten Märkten',
    calculateBtn: 'Mein Angebot Berechnen',
    calculating: 'Berechnung…',
  },
  results: {
    downloadPDF: 'PDF Herunterladen',
    print: 'Drucken',
    email: 'E-Mail',
    save: 'Speichern',
    saved: 'Gespeichert!',
    signInToSave: 'Melden Sie sich an, um Ihre Schätzungen zu speichern',
    totalCostRange: 'Geschätzte Gesamtkostenspanne:',
    costBreakdown: 'Kostenaufschlüsselung',
    baseInstallation: 'Grundmaterial & Installation:',
    included: 'Inklusive',
    tearOff: 'Abriss',
    layer: 'Schicht',
    layers: 'Schichten',
    pitchAdjustment: 'Neigungsanpassung',
    multiStory: 'Mehrgeschossige Arbeit',
    chimneyFlashing: 'Schornsteinabdichtung',
    skylightFlashing: 'Dachfensterabdichtung',
    complexityFee: 'Komplexitätsgebühr',
    valleys: 'Kehlen',
    urgencyPremium: 'Dringlichkeitszuschlag',
    estimateNote: '*Schätzungen für {sqft} sq ft in {postalLabel} {zip} ({country}). Währung: {symbol}.',
    materialComparison: 'Materialvergleichstabelle',
    material: 'Material',
    costPerSqFt: 'Kosten/sq ft',
    lifespan: 'Lebensdauer',
    bestFor: 'Geeignet Für',
    keyBenefits: 'Hauptvorteile',
    ecoNote: '= Umweltfreundliche Option mit Nachhaltigkeitsvorteilen',
    selected: 'Ausgewählt',
    shareTitle: 'Schätzung per E-Mail Teilen',
    recipientEmail: 'E-Mail-Adresse des Empfängers',
    emailPlaceholder: 'E-Mail-Adresse eingeben',
    recipientNote: 'Hinweis: Der Empfänger muss ein registrierter Benutzer sein',
    whatWillBeSent: 'Was gesendet wird:',
    pdfEstimate: 'Vollständige PDF-Schätzung mit Kostenaufschlüsselung',
    maintenanceChart: '30-Jahres-Wartungskosten-Vergleichsdiagramm',
    projectSpecs: 'Detaillierte Projektspezifikationen',
    cancel: 'Abbrechen',
    sendEmail: 'E-Mail Senden',
    sending: 'Senden…',
    emailSentSuccess: 'E-Mail erfolgreich gesendet!',
    emailFailed: 'E-Mail konnte nicht gesendet werden. Der Empfänger muss ein registrierter Benutzer sein.',
    maintenanceTitle: '30-Jahres-Wartungskosten-Vergleich',
    maintenanceSubtitle: 'Gesamtbetriebskosten umfassen Installation + Wartung über 30 Jahre.',
    installCost: 'Installationskosten',
    maintenanceCost: '30 Jahre Wartung',
    totalOwnership: 'Gesamtkosten 30 Jahre',
    maintenanceDetails: 'Wartungsdetails',
    yourChoice: 'Ihre Wahl',
    tipTitle: 'Kluger Investitionstipp:',
    tipBody: 'Materialien wie Metall, Schiefer und Recyceltes Metall haben höhere Anfangskosten, aber deutlich geringere Wartungskosten über 30 Jahre.',
    maintenanceNote: '* Wartungskosten sind Schätzungen basierend auf Branchendurchschnittswerten für ein {sqft} sq ft Dach.',
    ecoFriendly: 'Umweltfreundlich',
  },
  ai: {
    title: '🤖 KI-Materialempfehler',
    subtitle: 'Erhalten Sie personalisierte Empfehlungen basierend auf Ihrem Klima und Budget',
    climateLabel: 'Ihr Klima',
    climatePlaceholder: 'Klima auswählen',
    budgetLabel: 'Budgetrahmen',
    budgetPlaceholder: 'Budget auswählen',
    roofTypeLabel: 'Dachart (optional)',
    roofTypePlaceholder: 'z.B. Wohngebäude, Gewerbe, Flachdach',
    priorityLabel: 'Ihre Priorität',
    getRecommendation: 'KI-Empfehlung Erhalten',
    getting: 'Empfehlung wird ermittelt…',
    recommendedFor: 'Empfohlen für Ihr {climate} Klima & {budget} Budget',
    applyMaterial: 'Material Anwenden',
    applied: '✓ Angewendet!',
    error: 'Empfehlung konnte nicht ermittelt werden. Bitte versuchen Sie es erneut.',
    climateRequired: 'Bitte wählen Sie ein Klima',
    budgetRequired: 'Bitte wählen Sie einen Budgetrahmen',
    hotHumid: '☀️ Heiß & Feucht',
    coldSnowy: '❄️ Kalt & Schneereich',
    temperate: '🌤️ Gemäßigt',
    desert: '🏜️ Wüste / Trocken',
    coastal: '🌊 Küstengebiet',
    mixed: '🌦️ Gemischte Jahreszeiten',
    durability: '💪 Langlebigkeit',
    energySavings: '⚡ Energieeinsparungen',
    aesthetics: '🎨 Ästhetik',
    ecoFriendly: '🌱 Umweltfreundlich',
    lowMaintenance: '🔧 Geringer Wartungsaufwand',
  },
  nav: {
    home: 'Startseite',
    metal: 'Metallbedachung',
    commercial: 'Gewerbe',
    repair: 'Reparatur',
    virtual: 'Virtuelle Schätzung',
    blog: 'Blog',
    savedEstimates: 'Gespeicherte Schätzungen',
    signIn: 'Anmelden',
    signOut: 'Abmelden',
    language: 'Sprache',
  },
}

const pt: Translation = {
  locale: 'pt',
  name: 'Português',
  flag: '🇧🇷',
  form: {
    country: 'País',
    squareFootage: 'Área do Telhado (pés²)',
    squareFootageHelp: 'Casas comuns: 1.500–3.000 pés quadrados',
    material: 'Material do Telhado',
    removeOldRoof: 'Remover Telhado Antigo?',
    yes: 'Sim',
    no: 'Não',
    existingLayers: 'Camadas Existentes',
    layer1: '1 Camada',
    layer2: '2 Camadas',
    layer3: '3+ Camadas',
    numStories: 'Número de Andares',
    story1: '1 Andar',
    story2: '2 Andares',
    story3: '3+ Andares',
    roofPitch: 'Inclinação do Telhado',
    pitchLow: 'Baixa (2:12 – 4:12)',
    pitchMedium: 'Média (4:12 – 8:12)',
    pitchSteep: 'Alta (8:12+)',
    pitchHelp: 'Telhados mais inclinados aumentam os custos de mão de obra',
    roofCondition: 'Estado Atual do Telhado',
    conditionGood: 'Bom – Desgaste mínimo',
    conditionFair: 'Regular – Alguns danos',
    conditionPoor: 'Ruim – Problemas significativos',
    chimneys: 'Número de Chaminés',
    chimneysHelp: 'Calhas: ~{symbol}350 por chaminé',
    skylights: 'Número de Claraboias',
    skylightsHelp: 'Calhas: ~{symbol}250 por claraboia',
    roofComplexity: 'Complexidade do Telhado (Vales)',
    valleysFew: 'Poucos Vales – Design Simples',
    valleysModerate: 'Moderado – Casa Padrão',
    valleysMany: 'Muitos Vales – Design Complexo',
    valleysHelp: 'Mais vales aumentam a complexidade de instalação',
    timeline: 'Cronograma do Projeto',
    timelineFlexible: 'Flexível – Planejamento futuro',
    timelineSoon: 'Em breve – Dentro de 2–4 semanas',
    timelineUrgent: 'Urgente – Reparo de emergência',
    timelineHelp: 'Prazos urgentes podem incluir taxas adicionais',
    solarTooltipTitle: 'ROI a Longo Prazo e Benefícios Fiscais',
    solarFederalCredit: 'Crédito Fiscal Federal: 30% dos custos de instalação (até 2032)',
    solarEnergySavings: 'Economia de Energia: R$1.000–R$2.000/ano em média',
    solarPayback: 'Período de Retorno: 10–15 anos com incentivos',
    solarPropertyValue: 'Valor do Imóvel: Aumenta o valor em 4–6%',
    greenTooltipTitle: 'Benefícios de Sustentabilidade e Incentivos',
    greenTaxCredits: 'Créditos Fiscais: Varia por estado; verifique incentivos locais',
    greenEnergySavings: 'Economia de Energia: Redução de 20–30% nos custos de resfriamento',
    greenStormwater: 'Créditos de Água da Chuva: Muitas cidades oferecem descontos fiscais',
    greenLifespan: 'Vida Útil: Estende a membrana do telhado 2–3x (30–50 anos)',
    greenPropertyValue: 'Valor do Imóvel: Preço premium em mercados eco-conscientes',
    calculateBtn: 'Calcular Meu Orçamento',
    calculating: 'Calculando…',
  },
  results: {
    downloadPDF: 'Baixar PDF',
    print: 'Imprimir',
    email: 'E-mail',
    save: 'Salvar',
    saved: 'Salvo!',
    signInToSave: 'Faça login para salvar seus orçamentos',
    totalCostRange: 'Faixa de Custo Total Estimado:',
    costBreakdown: 'Detalhamento de Custos',
    baseInstallation: 'Material Base e Instalação:',
    included: 'Incluído',
    tearOff: 'Remoção',
    layer: 'camada',
    layers: 'camadas',
    pitchAdjustment: 'Ajuste de Inclinação',
    multiStory: 'Mão de Obra Multi-Andar',
    chimneyFlashing: 'Calha de Chaminé',
    skylightFlashing: 'Calha de Claraboia',
    complexityFee: 'Taxa de Complexidade',
    valleys: 'vales',
    urgencyPremium: 'Adicional de Urgência',
    estimateNote: '*Estimativas baseadas em {sqft} pés² em {postalLabel} {zip} ({country}). Moeda: {symbol}.',
    materialComparison: 'Tabela Comparativa de Materiais',
    material: 'Material',
    costPerSqFt: 'Custo/pé²',
    lifespan: 'Vida Útil',
    bestFor: 'Ideal Para',
    keyBenefits: 'Benefícios Principais',
    ecoNote: '= Opção ecológica com benefícios de sustentabilidade',
    selected: 'Selecionado',
    shareTitle: 'Compartilhar Orçamento por E-mail',
    recipientEmail: 'E-mail do Destinatário',
    emailPlaceholder: 'Digite o endereço de e-mail',
    recipientNote: 'Nota: O destinatário deve ser um usuário registrado',
    whatWillBeSent: 'O que será enviado:',
    pdfEstimate: 'Orçamento PDF completo com detalhamento de custos',
    maintenanceChart: 'Gráfico comparativo de manutenção de 30 anos',
    projectSpecs: 'Especificações detalhadas do projeto',
    cancel: 'Cancelar',
    sendEmail: 'Enviar E-mail',
    sending: 'Enviando…',
    emailSentSuccess: 'E-mail enviado com sucesso!',
    emailFailed: 'Falha ao enviar. O destinatário deve ser um usuário registrado.',
    maintenanceTitle: 'Comparação de Custos de Manutenção em 30 Anos',
    maintenanceSubtitle: 'Custos totais incluem instalação + manutenção em 30 anos.',
    installCost: 'Custo de Instalação',
    maintenanceCost: 'Manutenção 30 Anos',
    totalOwnership: 'Custo Total 30 Anos',
    maintenanceDetails: 'Detalhes de Manutenção',
    yourChoice: 'Sua Escolha',
    tipTitle: 'Dica de Investimento Inteligente:',
    tipBody: 'Materiais como Metal, Ardósia e Metal Reciclado têm custos iniciais mais altos, mas manutenção significativamente menor em 30 anos.',
    maintenanceNote: '* Os custos de manutenção são estimativas baseadas em médias do setor para um telhado de {sqft} pés².',
    ecoFriendly: 'Ecológico',
  },
  ai: {
    title: '🤖 Recomendador de Materiais IA',
    subtitle: 'Obtenha sugestões personalizadas com base no seu clima e orçamento',
    climateLabel: 'Seu Clima',
    climatePlaceholder: 'Selecione seu clima',
    budgetLabel: 'Faixa de Orçamento',
    budgetPlaceholder: 'Selecione seu orçamento',
    roofTypeLabel: 'Tipo de Telhado (opcional)',
    roofTypePlaceholder: 'ex. residencial, comercial, plano',
    priorityLabel: 'Sua Prioridade',
    getRecommendation: 'Obter Recomendação IA',
    getting: 'Obtendo recomendação…',
    recommendedFor: 'Recomendado para o seu clima {climate} e orçamento {budget}',
    applyMaterial: 'Aplicar Material na Calculadora',
    applied: '✓ Aplicado!',
    error: 'Não foi possível obter a recomendação. Tente novamente.',
    climateRequired: 'Por favor selecione um clima',
    budgetRequired: 'Por favor selecione uma faixa de orçamento',
    hotHumid: '☀️ Quente e Úmido',
    coldSnowy: '❄️ Frio e Nevado',
    temperate: '🌤️ Temperado',
    desert: '🏜️ Desértico / Árido',
    coastal: '🌊 Costeiro',
    mixed: '🌦️ Estações Mistas',
    durability: '💪 Durabilidade',
    energySavings: '⚡ Economia de Energia',
    aesthetics: '🎨 Estética',
    ecoFriendly: '🌱 Ecológico',
    lowMaintenance: '🔧 Baixa Manutenção',
  },
  nav: {
    home: 'Início',
    metal: 'Telhas Metálicas',
    commercial: 'Comercial',
    repair: 'Reparos',
    virtual: 'Orçamento Virtual',
    blog: 'Blog',
    savedEstimates: 'Orçamentos Salvos',
    signIn: 'Entrar',
    signOut: 'Sair',
    language: 'Idioma',
  },
}

export const translations: Record<Locale, Translation> = { en, es, fr, de, pt }

export const localeList: { locale: Locale; name: string; flag: string }[] = [
  { locale: 'en', name: 'English', flag: '🇺🇸' },
  { locale: 'es', name: 'Español', flag: '🇪🇸' },
  { locale: 'fr', name: 'Français', flag: '🇫🇷' },
  { locale: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { locale: 'pt', name: 'Português', flag: '🇧🇷' },
]
